import { inject, Injectable, InjectionToken } from '@angular/core';

import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MatDateRangeSelectionStrategy
} from '@angular/material/datepicker';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

@Injectable()
export class BrazilianDateAdapter extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.indexOf('/') > -1) {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = Number(parts[0]);
        const month = Number(parts[1]) - 1; // Month is 0-indexed
        const year = Number(parts[2]);

        // Validate the parsed values
        if (!isNaN(day) && !isNaN(month) && !isNaN(year) && year > 1900 && year < 2100) {
          return new Date(year, month, day);
        }
      }
    }
    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (displayFormat === 'input') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return super.format(date, displayFormat);
  }
}

@Injectable({ providedIn: 'root' })
export class ToggleableRangeSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  private fourteenDay = inject(DayRangeSelectionStrategy<D>);
  private defaultStrategy = inject(DefaultMatCalendarRangeStrategy<D>);

  private enabled = true;

  setEnabled(val: boolean) {
    this.enabled = val;
  }

  // ---- Overloads to support older + newer Angular Material versions ----
  selectionFinished(date: D | null): DateRange<D>;
  selectionFinished(date: D | null, currentRange: DateRange<D>, event?: Event): DateRange<D>;
  selectionFinished(date: D | null, currentRange?: DateRange<D>, _event?: Event): DateRange<D> {
    if (this.enabled) {
      return this.fourteenDay.selectionFinished(date, currentRange as any, _event);
    } else {
      const range = currentRange ?? new DateRange<D>(null, null);
      if (
        (!range.start || (range.start && range.end)) &&
        (range.start as any)?.getTime?.() === (date as any)?.getTime?.()
      ) {
        return new DateRange<D>(null, null) as DateRange<any>;
      }

      return this.defaultStrategy.selectionFinished(date, currentRange as any);
    }
  }

  createPreview(activeDate: D | null): DateRange<D>;
  createPreview(activeDate: D | null, currentRange: DateRange<D>, event?: Event): DateRange<D>;
  createPreview(activeDate: D | null, currentRange?: DateRange<D>, _event?: Event): DateRange<D> {
    return this.enabled
      ? this.fourteenDay.createPreview(activeDate, currentRange as any, _event)
      : this.defaultStrategy.createPreview(activeDate, currentRange as any);
  }
}

/**
 * Configure the minimum day **difference** between start and end.
 * For "13 days or more", use 13. (Inclusive span will be 14 days.)
 */
export const MIN_RANGE_DIFF_DAYS = new InjectionToken<number>('MIN_RANGE_DIFF_DAYS', {
  factory: () => 0
});

@Injectable()
export class DayRangeSelectionStrategy<D> implements MatDateRangeSelectionStrategy<D> {
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter<D>);
  private _minDiffDays = inject(MIN_RANGE_DIFF_DAYS);

  constructor() {
    this._dateAdapter.setLocale('pt-BR');
  }

  // ---- Overloads to support older + newer Angular Material versions ----
  selectionFinished(date: D | null): DateRange<D>;
  selectionFinished(date: D | null, currentRange: DateRange<D>, event?: Event): DateRange<D>;
  selectionFinished(date: D | null, currentRange?: DateRange<D>, _event?: Event): DateRange<D> {
    if (!date) return new DateRange<D>(null, null);

    const range = currentRange ?? new DateRange<D>(null, null);

    // Case 1: Starting a new selection.
    if (!range.start || (range.start && range.end)) {
      if ((range.start as any)?.getTime?.() === (date as any)?.getTime?.()) {
        return new DateRange<D>(null, null);
      }

      const start = this._dateAdapter.addCalendarDays(date, 0);
      return new DateRange(start, null);
    }

    // Case 2: Finishing selection: we already have a start but no end.
    const start = range.start;
    const end = this._dateAdapter.addCalendarDays(date, 0);
    return new DateRange(start, end);
  }

  createPreview(activeDate: D | null): DateRange<D>;
  createPreview(activeDate: D | null, currentRange: DateRange<D>, event?: Event): DateRange<D>;
  createPreview(activeDate: D | null, currentRange?: DateRange<D>, _event?: Event): DateRange<D> {
    if (!activeDate) return new DateRange<D>(null, null);

    const range = currentRange ?? new DateRange<D>(null, null);

    // Preview while picking the **start**.
    if (!range.start || (range.start && range.end)) {
      const start = activeDate;
      const end = this._addDays(start, this._minDiffDays);
      return new DateRange(start, end);
    }

    // Preview while picking the **end**.
    const start = range.start;
    const startDate = start;
    // difference in milliseconds
    const diffMs = (activeDate as any)?.getTime?.() - (startDate as any)?.getTime?.();

    // convert ms → days
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    const minEnd =
      diffDays < this._minDiffDays
        ? this._addDays(start, this._minDiffDays)
        : this._dateAdapter.addCalendarDays(activeDate, 0);
    return new DateRange(start, minEnd);
  }

  private _addDays = (date: D, days: number) => this._dateAdapter.addCalendarDays(date, days);
}
